// ═══════════════════════════════════════════════════════════════
// HOOK: usePositions - Con normalización de datos y refresh
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useCompany } from '../../../contexts/CompanyContext';
import api from '../../../api/config';

// ───────────────────────────────────────────────────────────────
// FUNCIÓN: Normalizar datos del backend al formato del frontend
// ───────────────────────────────────────────────────────────────
function normalizePosition(backendPosition) {
  if (!backendPosition) return null;

  // Parsear JSON si viene como string
  let relacionDepartamentos = [];
  if (backendPosition.relacion_departamentos) {
    try {
      relacionDepartamentos = typeof backendPosition.relacion_departamentos === 'string'
        ? JSON.parse(backendPosition.relacion_departamentos)
        : backendPosition.relacion_departamentos;
    } catch (error) {
      console.error('Error parsing relacion_departamentos:', error);
      relacionDepartamentos = [];
    }
  }

  return {
    id: backendPosition.id,
    nombre: backendPosition.nombre,
    
    // ⬇️ MAPEAR snake_case a camelCase
    departamentoId: backendPosition.department_id,
    departamento: backendPosition.departamento_nombre || 'Sin departamento',
    reportaA: backendPosition.reporta_a || null,
    
    // Campos descriptivos
    proposito: backendPosition.proposito || '',
    responsabilidades: backendPosition.responsabilidades || '',
    equipamiento: backendPosition.equipamiento || '',
    
    // Relaciones (convertir de JSON)
    relacionDepartamentos: relacionDepartamentos,
    
    // Condiciones
    condicionesTrabajo: backendPosition.condiciones_trabajo || '',
    
    // Requisitos del perfil
    nivelEducativo: backendPosition.nivel_educativo || '',
    experienciaRequerida: backendPosition.experiencia_requerida || '',
    conocimientosEspecificos: backendPosition.conocimientos_especificos || '',
    habilidadesBlandas: backendPosition.habilidades_blandas || '',
    habilidadesTecnicas: backendPosition.habilidades_tecnicas || '',
    certificacionesRequeridas: backendPosition.certificaciones_requeridas || '',
    idiomas: backendPosition.idiomas || '',
    
    // Compensación y horarios
    salarioRango: backendPosition.salario_rango || '',
    beneficios: backendPosition.beneficios || '',
    horario: backendPosition.horario || '',
    
    // Metadata
    estado: backendPosition.estado || 'activo',
    companyId: backendPosition.company_id,
    createdAt: backendPosition.created_at,
    updatedAt: backendPosition.updated_at
  };
}

// ───────────────────────────────────────────────────────────────
// FUNCIÓN: Convertir datos del frontend al formato del backend
// ───────────────────────────────────────────────────────────────
function denormalizePosition(frontendPosition) {
  if (!frontendPosition) return null;

  return {
    nombre: frontendPosition.nombre,
    
    // ⬇️ MAPEAR camelCase a snake_case
    department_id: frontendPosition.departamentoId,
    reporta_a: frontendPosition.reportaA || null,
    
    proposito: frontendPosition.proposito || '',
    responsabilidades: frontendPosition.responsabilidades || '',
    equipamiento: frontendPosition.equipamiento || '',
    
    // Convertir array a JSON
    relacion_departamentos: frontendPosition.relacionDepartamentos || [],
    
    condiciones_trabajo: frontendPosition.condicionesTrabajo || '',
    
    nivel_educativo: frontendPosition.nivelEducativo || '',
    experiencia_requerida: frontendPosition.experienciaRequerida || '',
    conocimientos_especificos: frontendPosition.conocimientosEspecificos || '',
    habilidades_blandas: frontendPosition.habilidadesBlandas || '',
    habilidades_tecnicas: frontendPosition.habilidadesTecnicas || '',
    certificaciones_requeridas: frontendPosition.certificacionesRequeridas || '',
    idiomas: frontendPosition.idiomas || '',
    
    salario_rango: frontendPosition.salarioRango || '',
    beneficios: frontendPosition.beneficios || '',
    horario: frontendPosition.horario || '',
    
    estado: frontendPosition.estado || 'activo'
  };
}

// ═══════════════════════════════════════════════════════════════
// HOOK PRINCIPAL
// ═══════════════════════════════════════════════════════════════
export default function usePositions() {
  const { currentCompany } = useCompany();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Función para cargar cargos
  const loadPositions = useCallback(async () => {
    if (!currentCompany?.id) {
      console.log('⚠️ No hay compañía seleccionada');
      setPositions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('📥 Cargando cargos...');
      const response = await api.get('/positions');
      
      // ⬇️ NORMALIZAR todos los cargos
      const normalizedPositions = (response.data || []).map(normalizePosition);
      
      console.log('✅ Cargos cargados y normalizados:', normalizedPositions.length);
      console.log('📊 Ejemplo de cargo normalizado:', normalizedPositions[0]);
      
      setPositions(normalizedPositions);
    } catch (error) {
      console.error('❌ Error cargando cargos:', error);
      setError(error.message);
      setPositions([]);
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id]);

  // Cargar al montar y cuando cambie la compañía
  useEffect(() => {
    loadPositions();
  }, [loadPositions]);

  // Función para refrescar (expuesta para uso externo)
  const refresh = useCallback(async () => {
    console.log('🔄 Refrescando cargos...');
    await loadPositions();
  }, [loadPositions]);

  // Agregar cargo
  const addPosition = async (positionData) => {
    try {
      console.log('➕ Creando cargo (frontend):', positionData);
      
      // ⬇️ CONVERTIR a formato backend
      const backendData = denormalizePosition(positionData);
      console.log('📤 Enviando al backend:', backendData);
      
      const response = await api.post('/positions', backendData);
      
      console.log('✅ Cargo creado:', response.data);
      
      // Refrescar lista
      await refresh();
      
      return normalizePosition(response.data);
    } catch (error) {
      console.error('❌ Error creando cargo:', error);
      throw error;
    }
  };

  // Actualizar cargo
  const updatePosition = async (id, positionData) => {
    try {
      console.log('✏️ Actualizando cargo (frontend):', id, positionData);
      
      // ⬇️ CONVERTIR a formato backend
      const backendData = denormalizePosition(positionData);
      console.log('📤 Enviando al backend:', backendData);
      
      const response = await api.put(`/positions/${id}`, backendData);
      
      console.log('✅ Cargo actualizado:', response.data);
      
      // Refrescar lista
      await refresh();
      
      return normalizePosition(response.data);
    } catch (error) {
      console.error('❌ Error actualizando cargo:', error);
      throw error;
    }
  };

  // Eliminar cargo
  const deletePosition = async (id) => {
    try {
      console.log('🗑️ Eliminando cargo:', id);
      await api.delete(`/positions/${id}`);
      
      console.log('✅ Cargo eliminado');
      
      // Refrescar lista
      await refresh();
    } catch (error) {
      console.error('❌ Error eliminando cargo:', error);
      
      if (error.response?.data?.error) {
        alert(`❌ ${error.response.data.error}`);
      } else {
        alert('❌ Error al eliminar el cargo');
      }
      
      throw error;
    }
  };

  return {
    positions,
    loading,
    error,
    addPosition,
    updatePosition,
    deletePosition,
    refresh
  };
}
